import React, { useState } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Divider,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  AccountCircle,
  Email as EmailIcon,
  Notifications as NotificationsIcon,
} from "@mui/icons-material";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useGetUserQuery } from "state/api";
import Logo from "../../../assets/logo_ita.png";

const DRAWER_WIDTH = 280;

function AdminLayout() {
  const theme = useTheme();
  const isNonMobile = useMediaQuery(theme.breakpoints.up("lg"));
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const userId = useSelector((state) => state.global.userId);
  const userRole = useSelector((state) => state.global.userRole);
  const { data: user } = useGetUserQuery(userId);

  // Check if user is admin
  if (userRole !== "admin") {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            padding: "3rem",
            backgroundColor: "white",
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
            maxWidth: 400,
          }}
        >
          <Typography
            variant="h4"
            sx={{ color: "#e53e3e", mb: 2, fontWeight: 600 }}
          >
            Accès refusé
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Vous n'avez pas les permissions nécessaires pour accéder à cette
            page.
          </Typography>
        </Box>
      </Box>
    );
  }

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const menuItems = [
    {
      text: "Gestion des utilisateurs",
      icon: <PeopleIcon />,
      path: "/admin/users",
      active: location.pathname.includes("/admin/users"),
    },
    {
      text: "Envoi d'emails en masse",
      icon: <EmailIcon />,
      path: "/admin/bulk-email",
      active: location.pathname.includes("/admin/bulk-email"),
    },
    {
      text: "Notifications push en masse",
      icon: <NotificationsIcon />,
      path: "/admin/bulk-notifications",
      active: location.pathname.includes("/admin/bulk-notifications"),
    },
  ];

  const drawer = (
    <Box sx={{ height: "100%", backgroundColor: "#1e293b" }}>
      {/* Logo/Brand */}
      <Box
        sx={{
          height: 64, // Match Toolbar height
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          backgroundColor: "#0f172a",
          px: 2,
        }}
      >
        <Box
          component="img"
          src={Logo}
          alt="Info Trafic Algérie - ITA"
          sx={{ mr: 2, display: "flex", height: 40, cursor: "pointer" }}
        />
        <Box sx={{ textAlign: "left" }}>
          <Typography
            variant="h6"
            sx={{
              color: "white",
              fontWeight: 600,
              fontSize: "1rem",
              lineHeight: 1.2,
            }}
          >
            Tableau de bord
          </Typography>
        </Box>
      </Box>

      {/* Navigation */}
      <List sx={{ px: 2, py: 3 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              onClick={() => navigate(item.path)}
              sx={{
                borderRadius: "12px",
                color: item.active ? "#3b82f6" : "#94a3b8",
                backgroundColor: item.active
                  ? "rgba(59, 130, 246, 0.1)"
                  : "transparent",
                "&:hover": {
                  backgroundColor: "rgba(59, 130, 246, 0.05)",
                  color: "#3b82f6",
                },
                transition: "all 0.2s ease",
                py: 1.5,
              }}
            >
              <ListItemIcon
                sx={{
                  color: "inherit",
                  minWidth: 40,
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: "0.95rem",
                  fontWeight: item.active ? 600 : 500,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", height: "100vh", backgroundColor: "#f8fafc" }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { lg: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { lg: `${DRAWER_WIDTH}px` },
          backgroundColor: "white",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { lg: "none" }, color: "#1e293b" }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                color: "#1e293b",
                fontWeight: 600,
              }}
            >
              {menuItems.find((item) => item.active)?.text || "Tableau de bord"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={handleProfileMenuOpen} sx={{ p: 0.5 }}>
              <Avatar
                src={user?.img}
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: "#3b82f6",
                  fontSize: "1rem",
                  fontWeight: 600,
                }}
              >
                {user?.pseudo?.charAt(0)?.toUpperCase() || "A"}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: "12px",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
            minWidth: 200,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e2e8f0" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {user?.pseudo || "Admin"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.email}
          </Typography>
        </Box>
        <MenuItem onClick={() => navigate("/home")} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          Revenir vers le site
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleMenuClose} sx={{ py: 1.5, color: "#e53e3e" }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" sx={{ color: "#e53e3e" }} />
          </ListItemIcon>
          Déconnexion
        </MenuItem>
      </Menu>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { lg: DRAWER_WIDTH }, flexShrink: { lg: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", lg: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
              border: "none",
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", lg: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
              border: "none",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { lg: `calc(100% - ${DRAWER_WIDTH}px)` },
          overflow: "auto",
        }}
      >
        <Toolbar /> {/* Spacer for AppBar */}
        <Outlet />
      </Box>
    </Box>
  );
}

export default AdminLayout;
